import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import { GlobalStyles }  from './theme/GlobalStyles';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth }       from './contexts/AuthContext';
import { DrawerProvider, useDrawer }   from './contexts/DrawerContext';
import { FiltersProvider, useFilters } from './contexts/FiltersContext';

import { AppRoutes }  from './routes/routeDefinitions';
import HeaderMenu     from './utils/HeaderMenu';
import Footer         from './utils/Footer';
import PageTracker    from './utils/PageTracker';
import SidebarDrawer  from './components/SidebarDrawer';
import { useDocumentTitle } from './hooks/useDocumentTitle';

import { initGA, logPageView } from './utils/analytics';
import { isProd } from './utils/posthogClient';
import {
  getValidFunnelToken,
  EXECEDGE_LAST_ROUTE_TOKEN_REQUIRED,
} from './pages/ExecEdge-Funnel/api';
import CandidateOnboardingModal, {
  ONBOARDING_MODAL_SKIP_KEY,
  useCandidateOnboardingGate,
} from './Home/CandidateOnboardingModal';
import {
  clearPrivacyAcceptanceCache,
  getPrivacyAcceptance,
  privacyAcceptanceUserKey,
  readCachedPrivacyAcceptance,
} from './Privacy/api';
import { resolvePostLoginPath } from './utils/returnTo';

const EXECEDGE_LAST_ROUTE_KEY = 'execedge_last_route';
const EXECEDGE_TRACKED_ROUTES = new Set([
  '/eedge/predemo',
  '/eedge/demo',
  '/eedge/thank-you',
  '/eedge/program-plan',
  '/eedge/faqs',
]);

// ── Root — providers only ─────────────────────────────────────────────────────
const App = () => (
  <AuthProvider>
    <DrawerProvider>
      <FiltersProvider>
        <Router future={{ v7_relativeSplatPath: true }}>
          <ThemeProvider>
            <GlobalStyles />
            <AppShell />
          </ThemeProvider>
        </Router>
      </FiltersProvider>
    </DrawerProvider>
  </AuthProvider>
);

// ── Shell — layout + analytics + redirects only ───────────────────────────────
const AppShell = () => {
  const { user, logout } = useAuth();
  const { openDrawer }   = useDrawer();
  const { onFiltersClick } = useFilters();
  const navigate  = useNavigate();
  const location  = useLocation();

  useDocumentTitle();

  const resolvedUser     = user ?? { role: 'guest' };
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const {
    loading: onboardingGateLoading,
    required: profileIncomplete,
  } = useCandidateOnboardingGate(resolvedUser);
  const isExecEdgeFunnel = location.pathname.startsWith('/eedge');
  const isApplyPage      = location.pathname.startsWith('/apply/');
  const isJoinWhatsAppPage = location.pathname.startsWith('/join-whatsapp');
  const isLoginPage      = location.pathname === '/login';
  const isLegalAcceptPage = location.pathname === '/legal/accept';
  const isAdminPrivacyPage = location.pathname === '/admin/privacy' || location.pathname.startsWith('/admin/privacy/');
  const legalExempt = resolvedUser.role === 'guest'
    || resolvedUser.role === 'admin'
    || isLegalAcceptPage
    || location.pathname.startsWith('/legal/documents')
    || location.pathname === '/login'
    || location.pathname.startsWith('/apply/')
    || location.pathname.startsWith('/admin');
  const acceptanceUserKey = privacyAcceptanceUserKey(resolvedUser);
  const cachedAcceptance = acceptanceUserKey ? readCachedPrivacyAcceptance(acceptanceUserKey) : null;
  const acceptedThisSession = Boolean(
    cachedAcceptance && !cachedAcceptance.acceptanceRequired && !cachedAcceptance.reAcceptanceRequired,
  );
  const [legalDecision, setLegalDecision] = useState({ userKey: '', status: 'pending' });
  const legalStatus = legalExempt
    ? 'clear'
    : acceptedThisSession
      ? 'clear'
      : legalDecision.userKey === acceptanceUserKey
        ? legalDecision.status
        : 'pending';
  const isOnboardingPage = onboardingOpen;
  const isProfilePage    = location.pathname.includes('/candidate/profile');

  // Analytics — prod only, once. PostHog is initialized by PostHogProvider.
  useEffect(() => {
    if (!isProd) return;
    initGA();
  }, []);

  // Page view tracking
  useEffect(() => {
    logPageView(location.pathname);
  }, [location.pathname]);

  // Persist last visited route for ExecEdge resume flow
  useEffect(() => {
    const cleanPath = location.pathname.length > 1
      ? location.pathname.replace(/\/+$/, '')
      : location.pathname;
    if (!EXECEDGE_TRACKED_ROUTES.has(cleanPath)) return;
    if (EXECEDGE_LAST_ROUTE_TOKEN_REQUIRED.has(cleanPath) && !getValidFunnelToken()) {
      return;
    }
    localStorage.setItem(EXECEDGE_LAST_ROUTE_KEY, cleanPath);
  }, [location.pathname]);

  // Auth redirects. Wait until the legal check finishes so the profile does not flash first.
  useEffect(() => {
    if (resolvedUser.role === 'guest') return;
    if (legalStatus !== 'clear') return;
    if (resolvedUser.role === 'admin' && location.pathname === '/legal/accept') {
      navigate('/candidate/home', { replace: true });
      return;
    }
    if (location.pathname === '/') {
      navigate('/candidate/home');
      return;
    }
    if (location.pathname === '/login') {
      navigate(resolvePostLoginPath(null, resolvedUser.role), { replace: true });
    }
  }, [resolvedUser, location.pathname, navigate, legalStatus]);

  useEffect(() => {
    if (!acceptanceUserKey) return;
    const cached = readCachedPrivacyAcceptance(acceptanceUserKey);
    if (cached && !cached.acceptanceRequired && !cached.reAcceptanceRequired) {
      setLegalDecision({ userKey: acceptanceUserKey, status: 'clear' });
      return;
    }
    let cancelled = false;
    setLegalDecision({ userKey: acceptanceUserKey, status: 'pending' });
    getPrivacyAcceptance(acceptanceUserKey)
      .then((data) => {
        if (cancelled) return;
        const required = Boolean(data?.acceptanceRequired || data?.reAcceptanceRequired);
        setLegalDecision({ userKey: acceptanceUserKey, status: required ? 'required' : 'clear' });
      })
      .catch(() => {
        if (!cancelled) setLegalDecision({ userKey: acceptanceUserKey, status: 'clear' });
      });
    return () => {
      cancelled = true;
    };
  }, [acceptanceUserKey]);

  useEffect(() => {
    if (legalExempt || legalStatus !== 'required') return;
    navigate('/legal/accept', { replace: true });
  }, [legalExempt, legalStatus, navigate]);

  useEffect(() => {
    if (legalStatus !== 'clear' || resolvedUser.role !== 'candidate') {
      setOnboardingOpen(false);
      return;
    }
    // Only show on candidate home — not ExecEdge, profile, or other routes
    const isCandidateHome =
      location.pathname === '/candidate/home' ||
      location.pathname === '/candidate/home/';
    if (!isCandidateHome) {
      setOnboardingOpen(false);
      return;
    }
    if (!resolvedUser.termsAccepted) return;
    if (onboardingGateLoading) return;
    // Once dismissed (skip or submit), never reopen this session
    if (sessionStorage.getItem(ONBOARDING_MODAL_SKIP_KEY) === resolvedUser.email) return;
    // Open once when gate says show; do not auto-close when fields get filled (autosave)
    if (profileIncomplete) {
      setOnboardingOpen(true);
    }
  }, [
    resolvedUser.role,
    resolvedUser.email,
    resolvedUser.termsAccepted,
    location.pathname,
    onboardingGateLoading,
    profileIncomplete,
    legalStatus,
  ]);

  const dismissOnboarding = () => {
    if (resolvedUser.email) {
      sessionStorage.setItem(ONBOARDING_MODAL_SKIP_KEY, resolvedUser.email);
    }
    setOnboardingOpen(false);
  };

  const handleLogout = () => {
    sessionStorage.removeItem(ONBOARDING_MODAL_SKIP_KEY);
    clearPrivacyAcceptanceCache(acceptanceUserKey);
    logout();
    localStorage.removeItem('themePreferenceSet');
    localStorage.removeItem('darkMode');
  };

  const holdForLegal = legalStatus !== 'clear';

  return (
    <>      
      <PageTracker />

      <div style={{
        display:       'flex',
        flexDirection: 'column',
        minHeight:     '100vh',
        maxWidth:      '100%',
        overflowX:     'hidden',
        background:    'var(--bg-page)',
        color:         'var(--text-primary)',
        fontFamily:    'var(--font-sans)',
      }}>
        {!isLoginPage && !holdForLegal && !isOnboardingPage && !isExecEdgeFunnel && !isApplyPage && !isJoinWhatsAppPage && (
          <HeaderMenu
            user={resolvedUser}
            handleLogout={handleLogout}
            onMenuClick={isLegalAcceptPage ? undefined : openDrawer}
            onFiltersClick={isLegalAcceptPage || isAdminPrivacyPage || isProfilePage ? null : onFiltersClick}
          />
        )}

        <SidebarDrawer />

        {/* Suspense boundary — shows nothing while lazy chunks load */}
        <React.Suspense fallback={
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'var(--bg-page)',
          color: 'var(--text-muted)',
          fontSize: '13px',
          fontFamily: 'var(--font-sans)',
        }}>
          {/* Empty — no spinner, no emoji, just blank while chunk loads */}
        </div>
      }>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {holdForLegal ? (
            <div style={{ flex: 1, minHeight: '100vh', background: 'var(--bg-page)' }} />
          ) : (
            <AppRoutes user={resolvedUser} />
          )}
        </div>
      </React.Suspense>

        {!isLoginPage && !holdForLegal && !isOnboardingPage && !isExecEdgeFunnel && <Footer />}
      </div>

      <CandidateOnboardingModal open={onboardingOpen && legalStatus === 'clear'} onClose={dismissOnboarding} />

      <Toaster
        position="top-center"
        gutter={12}
        toastOptions={{
          duration: 4000,
          style: {
            background:  'var(--bg-surface)',
            color:       'var(--text-primary)',
            border:      '1px solid var(--border-default)',
            fontFamily:  'var(--font-sans)',
            fontSize:    '13px',
            boxShadow:   'var(--shadow-lg)',
          },
          success: {
            duration: 3000,
            iconTheme: { primary: 'var(--text-success)', secondary: 'var(--bg-surface)' },
          },
          error: {
            duration: 4000,
            iconTheme: { primary: 'var(--text-danger)',  secondary: 'var(--bg-surface)' },
          },
        }}
      />
    </>
  );
};

export default App;